import { CSSProperties, FC, useEffect, useState } from 'react';
import * as prismicH from '@prismicio/helpers';
import classNames from 'classnames';
import { PrismicRichText } from '@prismicio/react';
import { useFilteredSliceItemsByLocationSlug, useIsMobile } from '@/hooks';
import {
  Container,
  Heading,
  Link,
  Placeholder,
  PrismicImageWithAlt,
  Slider,
} from '@/components';
import { BrandsSliceDefault, BrandsSliceGrid } from '@/types';
import { getSlicePadding, htmlSerializerWithProps } from '@/utils';
import styles from './brands.module.scss';

const BrandsCarousel: FC<BrandsSliceDefault> = ({ primary, items }) => {
  const { isMobile } = useIsMobile(1024);
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(items);
  const [isLoading, setIsLoading] = useState(true);
  const {
    spacing_between,
    spacing_between_mobile,
    brands_header,
    header_color,
    brand_name_color,
    image_size,
    image_size_mobile,
    link_text_see_all,
    link_text_color_see_all,
    bottom_spacing,
    background_color,
    border_radius,
    round_image,
    padding_section = 'large',
    full_width,
  } = primary;
  const borderRadius = round_image
    ? '100%'
    : border_radius
    ? `${border_radius}px`
    : '0';
  const paddingTop = !!background_color;
  const background = background_color ?? undefined;
  const backgroundImage = primary?.background_image;
  const backgroundStyleFixed = primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;

  const activeAutoplay = !!primary?.autoplay;
  const autoplayConfig = {
    delay: 5000,
    disableOnInteraction: false,
    stopOnLastSlide: false,
  };

  // Calculate slices per view based on screen width and items per view
  // If itemsPerView is not a number (undefined or "auto") or width is less than 1024 (mobile), use 'auto'
  // Otherwise, convert itemsPerView to a number (options are "auto", "2", "3", "4", "5")
  const itemsPerView = primary?.items_per_view;
  const slicesPerView =
    !itemsPerView || isMobile ? 'auto' : Number(itemsPerView);

  const brandsHeaderText = prismicH.asText(brands_header);
  const ariaLabel = brandsHeaderText
    ? `${brandsHeaderText} carousel`
    : 'Brands carousel';

  const title = prismicH.asText(brands_header);

  // Determine content alignment based on primary content_align value
  // Default to 'center' if not specified
  const itemsAlign = primary?.content_align;
  const contentAlign =
    itemsAlign === 'left'
      ? 'flex-start'
      : itemsAlign === 'right'
      ? 'flex-end'
      : 'center';

  useEffect(() => {
    if (filteredItems?.length) {
      setIsLoading(false);
    }
  }, [filteredItems]);

  const getImageSize = () => {
    if (isMobile) {
      return image_size_mobile ? `${image_size_mobile}px` : undefined;
    }

    return `${image_size ?? 280}px`;
  };

  const getSpaceBetween = () => {
    if (isMobile) {
      return spacing_between_mobile;
    }

    return spacing_between;
  };

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(getSlicePadding(padding_section), {
        [styles.spacing_bottom]: bottom_spacing,
        [styles.padding_background]: paddingTop && background,
        ['background__fixed_cover']: backgroundStyleFixed,
        ['background__contained']: !backgroundStyleFixed,
      })}
      style={{
        background: background_color!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Slider
        activatedHover={false}
        activeAutoplay={activeAutoplay}
        ariaLabel={ariaLabel}
        autoplay={activeAutoplay ? autoplayConfig : undefined}
        className={styles.brands_carousel}
        isNotPaddingMobile={full_width}
        loop={activeAutoplay}
        seeAllElement={
          <Link
            className={styles['brands__see-all']}
            href="/brands"
            style={{ color: link_text_color_see_all ?? '' }}
          >
            {link_text_see_all || 'SHOP ALL'}
          </Link>
        }
        showSlidesOutsideContainer={full_width}
        sliderHeading={
          title ? (
            <Heading level={2} style={{ color: header_color ?? '' }}>
              {title}
            </Heading>
          ) : undefined
        }
        slidesPerView={slicesPerView}
        spaceBetween={getSpaceBetween() || 24}
      >
        {isLoading || !filteredItems?.length
          ? Array.from({ length: 30 }).map((_, index) => (
              <div
                className={styles.brands_carousel__placeholder_brand}
                key={index}
              >
                <Placeholder
                  className={styles.brands_carousel__placeholder_image}
                />
                <Placeholder
                  className={styles.brands_carousel__placeholder_title}
                />
              </div>
            ))
          : filteredItems.map(brand => (
              <Link
                className={styles.slide__main}
                href={brand?.link ?? ''}
                key={brand.brand_name}
                style={{
                  minWidth: getImageSize(),
                  alignItems: contentAlign,
                }}
              >
                {brand.brand_name && (
                  <Heading level={3} style={{ color: brand_name_color ?? '' }}>
                    {brand.brand_name}
                  </Heading>
                )}
                <figure
                  className={classNames(styles.brand_image, {
                    [styles.brand_image_placeholder]: !brand?.brand_image?.url,
                  })}
                  style={{ borderRadius, maxWidth: getImageSize() }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Logo"
                    field={brand.brand_image}
                    showPlaceholderIfEmpty
                    sizes="(min-width: 1200px) 158px, 
                                        (min-width: 992px) 170px, 
                                        (min-width: 768px) 157px, 
                                        (min-width: 576px) 156px, 50vw"
                    style={{
                      borderRadius,
                      objectFit: 'cover',
                    }}
                    title={brand?.brand_name ?? ''}
                  />
                </figure>
              </Link>
            ))}
      </Slider>
    </section>
  );
};

const BrandsGrid: FC<BrandsSliceGrid> = ({ primary, items }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(items);
  const [hovered, setHovered] = useState<boolean>();
  const { isMobile } = useIsMobile(1024);

  const {
    brands_header,
    bottom_spacing,
    background_color,
    border_radius,
    round_image,
    header_color,
    brand_name_color,
    cta_text_color,
    cta_text_color_hover,
    cta_background_color,
    cta_background_color_hover,
    cta_border,
    cta_border_hover,
    hide_cta,
  } = primary;
  const borderRadius = round_image
    ? '100%'
    : border_radius
    ? `${border_radius}px`
    : '0px';
  const paddingSection = primary?.padding_section;
  const paddingTop = !!background_color;
  const background = background_color ?? undefined;
  const backgroundImage = primary?.background_image?.url;
  const backgroundStyleFixed = primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;
  const maxWidth = primary?.items_max_width;
  const maxWidthMobile = primary?.items_max_width_mobile;
  const contentAlign = primary?.content_align || 'center';

  // Determine number of columns per row
  const columnsPerRow = primary?.columns_per_row;
  const brandsHeaderText = prismicH.asText(brands_header);

  const getItemMaxWidth = () => {
    if (isMobile) {
      return maxWidthMobile ? `${maxWidthMobile}px` : undefined;
    }

    return `${maxWidth ?? 280}px`;
  };

  return (
    <section
      aria-label={brandsHeaderText ? `${brandsHeaderText} grid` : 'Brands grid'}
      className={classNames(getSlicePadding(paddingSection), {
        [styles.spacing_bottom]: bottom_spacing,
        [styles.padding_background]: paddingTop && background,
        ['background__fixed_cover']: backgroundStyleFixed,
        ['background__contained']: !backgroundStyleFixed,
      })}
      style={{
        background: background_color!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Container className={styles.brands_grid}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: header_color })}
          field={brands_header}
        />
        <div
          className={styles.brands_grid__container}
          style={
            {
              '--columns-per-row': columnsPerRow,
              justifyItems: contentAlign,
            } as CSSProperties
          }
        >
          {filteredItems.map(brand => {
            const cardContent = (
              <>
                {brand?.brand_name && (
                  <p style={{ color: brand_name_color ?? '' }}>
                    {brand.brand_name}
                  </p>
                )}
                <figure
                  className={classNames(styles.image, {
                    [styles.image_placeholder]: !brand?.brand_image?.url,
                  })}
                  style={{ borderRadius }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Brand image"
                    field={brand.brand_image}
                    fill
                    showPlaceholderIfEmpty
                    sizes="(min-width: 1200px) 158px, 
                                        (min-width: 992px) 170px, 
                                        (min-width: 768px) 157px, 
                                        (min-width: 576px) 156px, 50vw"
                    style={{ borderRadius, objectFit: 'cover' }}
                    title={brand?.brand_name ?? ''}
                  />
                </figure>
                {!hide_cta && (
                  <span
                    onClick={() => setHovered(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setHovered(true);
                      }
                    }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    role="button"
                    style={{
                      backgroundColor: (hovered
                        ? cta_background_color_hover
                        : cta_background_color) as string,
                      color: (hovered
                        ? cta_text_color_hover
                        : cta_text_color) as string,
                      border: (hovered
                        ? cta_border_hover
                        : cta_border) as string,
                    }}
                    tabIndex={0}
                  >
                    {brand?.cta_text ?? 'Shop Now'}
                  </span>
                )}
              </>
            );
            return (
              <Link
                aria-label={`Go to ${brand?.brand_name ?? 'brand'}`}
                className={styles.brands_grid__container__brands}
                href={brand?.cta_link ?? '/'}
                key={brand.brand_name}
                style={{ maxWidth: getItemMaxWidth() }}
              >
                {cardContent}
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export { BrandsCarousel, BrandsGrid };
